import {
  Fragment,
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronUp,
  ChevronDown,
  Download,
  Filter,
  Maximize2,
  X,
  Search,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import {
  MOTION,
  useEntranceSuppressed,
  usePrefersReducedMotion,
  Sparkline,
  DeltaChip,
  HEAT_RAMP,
  PALETTE,
  parseValueDelta,
  parseDeltaOnly,
  TrendCell,
  trendMetricColor,
  resolveTone,
  toneStyle,
  TONES,
  type ParsedValueDelta,
  type Tone,
} from "@pervasivemind/bi-components";

import { SectionHeader } from "./SectionHeader";
import { FadeSwap } from "./motion/index";
import { isIsoDateString } from "./formatting";
import { crossFilterActiveValue } from "./filter-write";
import type { StateSpec } from "./types";

/**
 * Phase B — per-column visual render modes (mirror of the builder version).
 *
 * ``trend`` (redesign, fiscal comparison): cells whose SQL text is
 * "value ▲/▼ ±x.x%" render as bold value + micro-bar (∝ the row's max
 * across trend columns) + pastel delta chip. Non-parseable cells fall
 * back to the default renderer — style degrades, data never does.
 */
export type ColumnRender =
  | "default"
  | "heat"
  | "sparkline"
  | "status"
  | "progress"
  | "compare"
  | "trend"
  | "tone"
  | "chip"
  // Mixed columns (DSA 1.19, retailer table).
  | "fraction"
  | "delta_pp"
  | "mini_stack"
  | "action"
  | "state_cell";

/** One segment of a ``mini_stack`` cell: a row key and its colour. */
export interface StackPartDef {
  key: string;
  label?: DisplayLabel;
  /** The state this part shows (painted in that tone's v3 colour); absent
   *  = a category, painted from the series palette. */
  tone?: Tone | null;
}

export interface ColumnDef {
  key: string;
  label: string;
  format?: "currency" | "percent" | "number" | "text";
  /** Optional max width (e.g. "max-w-[220px] truncate") */
  truncate?: string;
  /** Phase B — visual render variant for the cell. Defaults to `default`. */
  render?: ColumnRender;
  /** Threshold bands for ``render: "tone"`` (epic v3 scorecards).
   *  REQUIRED when that render mode is used: a tone column with no
   *  bands would silently paint every cell neutral, which is the
   *  "wrong colour, no signal" failure this vocabulary exists to
   *  remove — so the cell renders a visible error marker instead. */
  thresholds?: Array<{ min: number | null; tone: Tone }>;
  /** Cross-filter target. When set (and ``onCellFilter`` is provided),
   * scalar cells in this column render as clickable and set the named
   * report filter to the cell's raw value. */
  filterKey?: string;
  /** Congela esta columna al scrollear horizontal.
   *
   *  Solo tiene sentido en las PRIMERAS columnas y en ese orden: las
   *  de identidad (id / marca / modelo / nombre), que son las que
   *  dicen QUE fila estas leyendo. Una tabla de 19 columnas scrollea
   *  ~1200px y sin esto el lector llega a la columna del retailer sin
   *  saber de que producto era la fila.
   *
   *  El `left` acumulado se MIDE del layout real (`useLayoutEffect` +
   *  `ResizeObserver`), no se declara: pedirle al autor que calcule
   *  offsets en px seria pedirle que adivine el ancho del contenido, y
   *  cualquier cambio de fuente o de dato los desalinea sin aviso. */
  sticky?: boolean;
  /** Ancho maximo de la columna, en px. Lo que no entra se corta con
   *  elipsis y el valor entero queda en el `title`.
   *
   *  Existe por las CONGELADAS: el ancho de una columna sale de su
   *  contenido, asi que un nombre de producto largo puede pedir 447px
   *  — y una franja fija de 802px sobre una ventana de 726 no deja
   *  lugar a ninguna de las columnas que el scroll deberia revelar
   *  (medido en staging). El ancho de una columna de identidad es una
   *  decision de diseno, no una medicion: por eso este SI se declara,
   *  al reves que el `left` de `sticky`. */
  maxWidth?: number;
  /** Key of another column in the SAME row, rendered as a muted second
   *  line under this cell: "38" over "de 209 en el retailer".
   *
   *  A figure and the denominator it is measured against belong in one
   *  cell. As two columns they double the header width and break the
   *  pairing — the reader has to re-associate them across two headers,
   *  on every row. The referenced column is normally authored
   *  ``hidden``, which keeps it in the SELECT (so free-text search
   *  still matches it) without giving it a header of its own. */
  subKey?: string;
  /** Formula line under the header label ("listados / universo"), so
   *  the denominator a percentage uses is readable without a tooltip. */
  subLabel?: DisplayLabel;
  /** ``render: "fraction"``: key of the row's denominator. The cell
   *  reads "67 / 96" with the share "70 %" on a muted line; without a
   *  usable denominator it renders the bare figure. */
  denominatorKey?: string;
  /** ``render: "mini_stack"``: the parts of the 100%-wide bar, in
   *  order. A part whose value is missing or ≤ 0 takes no width. */
  stack?: StackPartDef[];
  /** ``render: "state_cell"``: SQL token → label / tone / glyph /
   *  suffix. REQUIRED for that render; an undeclared token renders a
   *  visible error, never raw text. */
  states?: Record<string, StateSpec>;
  /** Key of another column of the row shown as a small pill after the
   *  value ("Hisense [YOUR BRAND]"); an empty value draws nothing. */
  badgeKey?: string;
  /** ``state_cell`` look: absent = pill; ``fill`` = the whole cell tinted
   *  with a 2px tone border on the left (DSA prototype v7 matrix). */
  cellStyle?: "fill";
}

interface DataTableProps {
  rows: Record<string, unknown>[];
  totalCount: number;
  sortBy: string;
  sortDir: "asc" | "desc";
  onSort: (column: string) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Fetch ALL rows for export (not just current page). */
  fetchAllRows?: () => Promise<Record<string, unknown>[]>;
  /** QA-F01: export generated on the SERVER (parts of 250 000 rows, a
   *  ZIP for 2+ parts). When set it wins over ``fetchAllRows``; a
   *  rejection shows the error and no file is saved. */
  exportAll?: (format: TableExportFormat) => Promise<ExportTableResult>;
  /** Free-text search (server-side via DuckDB ILIKE). */
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Optional column override. When omitted, uses Dashboard Explorer defaults. */
  columns?: ColumnDef[];
  /** Optional title override. Defaults to dashboard explorer's table title. */
  title?: string;
  /** Pre-resolved tooltip text for the table title (config ``info``). */
  info?: string;
  /** Optional subtitle override. Defaults to translated row count. */
  subtitle?: string;
  /** When true AND there are no rows yet, the empty body shows a
   * "loading data" message instead of the "no data" empty-state — so a
   * table that's still fetching (large query, cold cache) doesn't look
   * permanently empty. Defaults to false. */
  loading?: boolean;
  /** Optional export filename prefix. */
  exportName?: string;
  /** Iter 10b — row-drill click handler. When set, rows become clickable
   * and show a hover affordance. The handler receives the clicked row's
   * value for the column named by ``rowDrillKey``. */
  onRowClick?: (rowValue: string, row: Record<string, unknown>) => void;
  /** Column key whose value is passed to ``onRowClick``. Required when
   * ``onRowClick`` is set. */
  rowDrillKey?: string;
  /** When set, that row gets a "selected" visual treatment to show the
   * drill is open beneath it. Compared against ``row[rowDrillKey]``. */
  activeRowValue?: string | null;
  /** Optional row-height density. Accepted but currently rendered as a
   * passthrough — the table styling is fixed-compact. Builder PreviewModal
   * forwards a per-report density preference here for forward-compat. */
  density?: "compact" | "normal" | "comfortable";
  /** Override placeholder text shown in the search input. Defaults to
   * the translated ``table.search`` value. */
  searchPlaceholder?: string;
  /** Override the unit label used in the row-count subtitle (e.g.
   * "publicaciones" instead of the default "rows"). */
  rowLabel?: string;
  /** v1 finalization (iter 16): fires when the user toggles the
   * fullscreen mode. Parents (typically ``NamedDataTable`` or
   * ``GenericDashboardPage``) listen so they can request a larger
   * ``pageSize`` while fullscreen — fewer pagination round-trips
   * while the table has the whole viewport. The callback receives
   * the NEW value (``true`` = entering fullscreen). */
  onFullscreenChange?: (isFullscreen: boolean) => void;
  /** Controlled fullscreen (review #1 — fullscreen × drill). When set,
   * the prop is the source of truth and the toggle button only reports
   * through ``onFullscreenChange`` — the owner (``DashboardTable``) can
   * then EXIT fullscreen when a row-drill opens instead of stranding
   * the overlay inside the hidden table face. Absent → the historical
   * internal state (legacy consumers unchanged). */
  fullscreen?: boolean;
  /** Cross-filter: fires when the user clicks a cell in a column with
   * ``filterKey`` set. Receives the column's ``filterKey`` and the
   * cell's raw value stringified. The host owns the toggle semantics
   * (set / clear the report filter). */
  onCellFilter?: (filterKey: string, rawValue: string) => void;
  /** Current report filter values — used to highlight the cell whose
   * value IS the active cross-filter (and flip the tooltip to
   * "clear"). Only consulted for columns with ``filterKey``. */
  activeFilters?: Record<string, string>;
  /** Key of a boolean column flagging the "Total" row. That row is
   * pulled out of the page and pinned at the bottom — bold, outside
   * the zebra, not clickable — whatever the sort put it. The SQL emits
   * it (UNION ALL) so its numbers come from the same query as the rows
   * it totals. */
  totalRowKey?: string;
  /** Key of a column flagging rows to HIGHLIGHT: tinted, but they keep
   * their place in the sort (unlike the pinned total row). A boolean
   * flag tints neutral; a tone ("good" / "warn" / "bad") tints with it. */
  highlightRowKey?: string;
  /** Quick-filter chips in a strip under the header ("" = all). */
  quickFilters?: {
    options: Array<{ value: string; label: string }>;
    active: string;
    onChange: (value: string) => void;
  };
  /** Sort chips in the same strip; the active one matches the current
   * sort column + direction. */
  sortPresets?: {
    options: Array<{ label: string; sortBy: string; sortDir: "asc" | "desc" }>;
    activeSortBy: string | null;
    activeSortDir: "asc" | "desc";
    onSelect: (sortBy: string, sortDir: "asc" | "desc") => void;
  };
  /** Extra classes appended to the card container (non-fullscreen only).
   * Used by ``DashboardBody`` to stretch a ``row_span``-ned table card
   * (``min-h-0 flex-1``) so it fills its multi-row grid area. */
  className?: string;
  /** Uniform-grid PR-2 — the card fills its (fixed-height) parent:
   * ``flex h-full flex-col``, the row zone scrolls internally
   * (``flex-1 min-h-0 overflow-y-auto`` with the sticky thead) and the
   * pagination footer stays pinned (``shrink-0``). ``page_size`` keeps
   * its server-side semantics — the cell just bounds how many rows are
   * VISIBLE without scrolling. Changing to a page with fewer rows no
   * longer changes the card height (the grid cell owns it). */
  fillHeight?: boolean;
  /** Mobile stack mode — natural height CAPPED at this px (internal
   * scroll beyond it, same flex plumbing as ``fillHeight``). */
  capPx?: number;
  /** Accordion row-drill (``row_drill_mode: "accordion"``, the
   * per-report opt-in since 2026-09-09): renders the detail of the ACTIVE row
   * (``activeRowValue``) in an expansion row mounted right under it,
   * INSIDE the scrolling rows zone. The card / grid cell never grows —
   * what grows is the scrollable content. The callback receives the
   * max px the expansion may take (the rows zone height minus the
   * sticky header and two data rows, so the clicked row and its
   * detail always fit together; ``undefined`` until the zone has a
   * measured height) and must return the detail node (the host mounts
   * ``RowDrillPanel variant="accordion"`` with ``capPx`` = that max).
   * One expansion per table: it follows ``activeRowValue``. */
  renderRowExpansion?: (ctx: { maxPx: number | undefined }) => ReactNode;
  /** Re-runs the "reveal" scroll (``scrollIntoView block:nearest`` on
   * the expansion, then on its row) when it changes — the host bumps it
   * once the detail settles, because the expansion grows from its
   * loading skeleton to its real height at that moment. */
  expansionRevealKey?: string;
  /** Tasks: content of a trailing cell per row (the report flag or the
   * mark of a reported row). Absent = no extra column (the builder
   * Preview, and every table without ``row_identity``). Its clicks never
   * reach the row (no drill opens). Not rendered on the total row. */
  renderRowMark?: (row: Record<string, unknown>) => ReactNode;
}

/**
 * Fill-mode floor for the scrolling rows zone (uniform-grid review #4):
 * approx. the sticky header (~27px: th py-1.5 + 10px text line) plus
 * TWO compact data rows (~24px each: td py-1 + 11px text line). When a
 * grid cell is absurdly small for a table (a config below the
 * publish-time table MIN_H that predates the bound), the card
 * OVERFLOWS its cell visibly instead of rendering zero data rows — the
 * same philosophy as GenericChart's MIN_PLOT_PX: noisy, never silent.
 */
const MIN_ROWS_ZONE_PX = 76;
/**
 * Accordion expansion budget. The expansion may take the rows zone
 * height MINUS the sticky column header (~27px: th py-1.5 + 10px text
 * line) and TWO compact data rows (~24px each: td py-1 + 11px text
 * line) — the same anatomy `MIN_ROWS_ZONE_PX` is derived from — so the
 * clicked row plus its detail always fit in the zone together with one
 * neighbouring row for context. Below `EXPANSION_MIN_PX` the detail is
 * useless (the compact strip alone is ~32px and the loading skeleton
 * 96px), so an absurdly small cell lets the expansion OVERFLOW the zone
 * visibly (it scrolls) instead of crushing the detail to nothing —
 * noisy, never silent, same philosophy as `MIN_ROWS_ZONE_PX`.
 */
export const ROWS_ZONE_HEAD_PX = 27;
export const COMPACT_ROW_PX = 24;
export const EXPANSION_RESERVE_ROWS = 2;
export const EXPANSION_MIN_PX = 140;

/** Max px an accordion expansion may take inside a rows zone of the
 *  given measured height; `undefined` while the zone has no layout
 *  (jsdom, the first frame) — the expansion then renders uncapped
 *  until the ResizeObserver reports a real height. */
export function expansionMaxPx(zoneHeightPx: number): number | undefined {
  if (!(zoneHeightPx > 0)) return undefined;
  const budget =
    zoneHeightPx - ROWS_ZONE_HEAD_PX - EXPANSION_RESERVE_ROWS * COMPACT_ROW_PX;
  return Math.max(EXPANSION_MIN_PX, budget);
}
/** Techo de la franja congelada, como fraccion del ancho visible.
 *
 *  Congelar las columnas de identidad sirve para no perder de vista
 *  QUE fila estas leyendo. Pasado la mitad del ancho deja de servir:
 *  lo que queda para las otras 16 columnas es menos que la franja
 *  fija, y en el caso que lo destapo (802px fijos sobre 726 visibles)
 *  el scroll horizontal no revelaba absolutamente nada. El reporte de
 *  referencia se queda en ~32%. */
const MAX_STICKY_FRACTION = 0.5;

/**
 * Row cascade entrance (motion ronda 14): the tbody rows fade+rise in
 * top-to-bottom on the FIRST data landing only. ~20ms stagger per row,
 * compressed so the last row starts no later than `CAP - durBase`
 * (whole cascade ≤ 600ms). Compositor-only inline CSS on the `<tr>`s —
 * the tbody identity never changes, so the SWR pins (body nodes never
 * unmount) and the controlled sort/search/pagination stay intact.
 */
const ROW_CASCADE_STAGGER_MS = 20;
const ROW_CASCADE_CAP_MS = 600;

/** Column formats that read as numbers → right-aligned (UI polish
 *  2026-07: numeric AND ISO-date cells align right with tabular-nums;
 *  headers of numeric columns follow the cells). */
const NUMERIC_FORMATS: ReadonlySet<string> = new Set([
  "currency",
  "percent",
  "number",
]);

function isNumericColumn(col: ColumnDef): boolean {
  return NUMERIC_FORMATS.has(col.format ?? "");
}

/** Numeric formats plus the mixed cells that read as numbers ("67 / 96",
 *  "+3.2 pp"): right-aligned so the digits line up down the column. */
function isRightAlignedColumn(col: ColumnDef): boolean {
  return (
    isNumericColumn(col) ||
    col.render === "fraction" ||
    col.render === "delta_pp"
  );
}

const DEFAULT_COLUMNS: ColumnDef[] = [
  { key: "retailer_sku", label: "SKU", format: "text" },
  { key: "category", label: "Category", format: "text", truncate: "max-w-[90px] truncate" },
  { key: "listing_title", label: "Title", format: "text", truncate: "max-w-[180px] truncate" },
  { key: "annual_rev", label: "Ann Rev", format: "currency" },
  { key: "rev_3m", label: "Rev 3M", format: "currency" },
  { key: "1p_rev_3m", label: "1P 3M", format: "currency" },
  { key: "1p_rev_pct", label: "1P%", format: "percent" },
  { key: "3p_rev_3m", label: "3P 3M", format: "currency" },
  { key: "3p_rev_pct", label: "3P%", format: "percent" },
  { key: "woh", label: "WOH", format: "number" },
  { key: "is_1p", label: "1P", format: "text" },
  { key: "mpa_count", label: "MPA", format: "number" },
  { key: "non_mpa_count", label: "!MPA", format: "number" },
  { key: "buy_box_rate", label: "BB%", format: "percent" },
];

import { csvField, formatCellValue, formatNumberLocale } from "./formatting";
import {
  isBilingualJsonString,
  resolveLocalized,
  type DisplayLabel,
} from "./i18n-resolve";
import { useLang, type Lang } from "./i18n-binding";
import type { ExportTableResult, TableExportFormat } from "./dashboard-data";

export function DataTable({
  rows,
  totalCount,
  sortBy,
  sortDir,
  onSort,
  page,
  pageSize,
  onPageChange,
  fetchAllRows,
  exportAll,
  search,
  onSearchChange,
  searchPlaceholder,
  columns: columnsProp,
  title: titleProp,
  subtitle: subtitleProp,
  info,
  loading = false,
  exportName = "data",
  onRowClick,
  rowDrillKey,
  activeRowValue,
  onFullscreenChange,
  fullscreen: fullscreenProp,
  onCellFilter,
  activeFilters,
  totalRowKey,
  highlightRowKey,
  quickFilters,
  sortPresets,
  className,
  fillHeight = false,
  capPx,
  renderRowExpansion,
  expansionRevealKey,
  renderRowMark,
}: DataTableProps) {
  const { t } = useTranslation();
  const lang = useLang();
  const linkLabel = t("table.openLink");
  const columns = columnsProp ?? DEFAULT_COLUMNS;
  // Every full-width cell spans the data columns plus the mark column.
  const rowsZoneRef = useRef<HTMLDivElement | null>(null);
  // ── Columnas congeladas: los offsets se MIDEN ──────────────
  //
  // `position: sticky` necesita un `left` por columna, y el de la
  // columna N es la suma de los anchos de las N-1 anteriores. Ese
  // numero no existe hasta que el navegador hizo layout, asi que se
  // mide del <th> real y se re-mide en cada resize. La alternativa
  // (pedir anchos en el config) le pide al autor que adivine el ancho
  // del contenido y se desalinea con cualquier cambio de dato.
  const stickyCount = useMemo(() => {
    // Solo un PREFIJO puede congelarse: una columna fija en el medio
    // dejaria un hueco al scrollear. Se corta en la primera que no lo
    // pide.
    let n = 0;
    for (const c of columns) {
      if (!c.sticky) break;
      n += 1;
    }
    return n;
  }, [columns]);
  const headRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  // Column widths only grow while the column set stays the same: with
  // `table-auto` a sort or a refetch re-sized every column to its new
  // content and the view jumped (CLS 0.074 sorting "Días a entrega", QA
  // F23, 2026-10-26). Each header keeps the widest width it has had;
  // a new set of columns starts over.
  const colMinWidths = useRef<Record<string, number>>({});
  const colSetKey = columns.map((c) => c.key).join("|");
  const colSetRef = useRef(colSetKey);
  if (colSetRef.current !== colSetKey) {
    colSetRef.current = colSetKey;
    colMinWidths.current = {};
  }
  // An empty table (loading, or no data) is no measure of its columns:
  // `w-full` spreads the bare header across the whole card, ~215px each
  // in an 860px tile, and once kept those widths no longer fit the rows
  // when they land (Pricing-Retail subcategories, 2026-10-30: 954px of
  // columns in an 860px cell, the last one behind a scroll).
  useLayoutEffect(() => {
    if (rows.length === 0) return;
    headRefs.current.forEach((el, i) => {
      const key = columns[i]?.key;
      if (!el || !key) return;
      const width = el.getBoundingClientRect().width;
      if (width > (colMinWidths.current[key] ?? 0)) colMinWidths.current[key] = width;
    });
  });
  const [stickyOffsets, setStickyOffsets] = useState<number[]>([]);
  useEffect(() => {
    if (stickyCount === 0) {
      setStickyOffsets((prev) => (prev.length ? [] : prev));
      return;
    }
    const measure = () => {
      const next: number[] = [];
      let acc = 0;
      for (let i = 0; i < stickyCount; i += 1) {
        next.push(acc);
        acc += headRefs.current[i]?.getBoundingClientRect().width ?? 0;
      }
      // Ancho total 0 = no hubo layout (jsdom, un thead oculto, el
      // primer frame). Emitir `sticky` ahi pondria TODOS los `left` en
      // 0 y apilaria las columnas congeladas una sobre otra, tapando el
      // dato. Sin offsets reales se degrada a tabla normal, que es
      // ilegible-nada versus ilegible-tapado.
      if (acc <= 0) {
        setStickyOffsets((prev) => (prev.length ? [] : prev));
        return;
      }
      // Presupuesto: una franja fija mas ancha que la ventana tapa
      // TODAS las columnas que el scroll deberia revelar — medido en
      // staging, 802px congelados sobre 726px visibles, o sea que
      // scrollear no mostraba nada nuevo. Congelar sirve mientras
      // quede tabla que leer; pasado el techo se degrada a tabla
      // normal, donde el dato al menos es alcanzable.
      const visible = rowsZoneRef.current?.clientWidth ?? 0;
      if (visible > 0 && acc > visible * MAX_STICKY_FRACTION) {
        setStickyOffsets((prev) => (prev.length ? [] : prev));
        return;
      }
      setStickyOffsets((prev) =>
        prev.length === next.length && prev.every((v, i) => Math.abs(v - next[i]!) < 0.5)
          ? prev
          : next,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    for (let i = 0; i < stickyCount; i += 1) {
      const el = headRefs.current[i];
      if (el) ro.observe(el);
    }
    // Y el CONTENEDOR, que es la otra mitad del presupuesto.
    //
    // Medido en staging: angostar la zona de 1518 a 683px dejo la
    // franja fija en 78% del ancho visible y las columnas SIGUIERON
    // congeladas, porque los <th> no habian cambiado de ancho — el
    // contenido manda — asi que el observer nunca disparaba. El
    // presupuesto compara dos numeros y hay que mirar los dos: la
    // franja puede quedarse quieta mientras la pantalla se achica.
    if (rowsZoneRef.current) ro.observe(rowsZoneRef.current);
    return () => ro.disconnect();
  }, [stickyCount, columns, rows]);

  /** Estilo sticky de la celda `i`, o `undefined` cuando no lo es. */
  const stickyStyle = useCallback(
    (i: number): CSSProperties | undefined => {
      if (i >= stickyCount) return undefined;
      const left = stickyOffsets[i];
      // Sin medir todavia: NO se emite `position: sticky`. Con `left`
      // sin resolver las columnas se apilarian todas en 0 y taparian
      // el dato — peor que no congelar nada durante un frame.
      if (left === undefined) return undefined;
      return { position: "sticky", left, zIndex: 2 };
    },
    [stickyCount, stickyOffsets],
  );
  // Drillable tables advertise the row-drill with the "view detail"
  // hover pill rendered in the last cell (user decision 2026-07: the
  // PR #60 persistent trailing chevron column was reverted — Pablo
  // prefers the pill). The boolean still styles the trigger cell.
  const isDrillTable = onRowClick != null && rowDrillKey != null;
  // The «Reportar» flag lives INSIDE the «Ver detalle» cell, to the left
  // of its button, when the table has one (Pablo, 2026-10-30: a column
  // of its own never lined up). Without a detail column it gets its own
  // cell pinned to the right edge: in a table wider than its card a
  // trailing cell sat behind the horizontal scroll (Pricing-Retail
  // Master Matrix). Same border and shadow as the detail column.
  const markInDetail = isDrillTable && renderRowMark !== undefined;
  const markColumn = renderRowMark !== undefined && !isDrillTable;
  const markStickyClass =
    "sticky right-0 z-[3] border-l border-neutral-200 shadow-[-6px_0_8px_-6px_theme(colors.neutral.400)]";
  const detailWidthClass = markInDetail
    ? "w-44 min-w-44 max-sm:w-[100px] max-sm:min-w-[100px]"
    : "w-32 min-w-32 max-sm:w-[52px] max-sm:min-w-[52px]";
  // Every extra column (task mark, sticky row-detail action) widens the
  // colSpan of the expansion and empty rows: keep this the single count.
  const fullSpan = columns.length + (markColumn ? 1 : 0) + (isDrillTable ? 1 : 0);
  // Look v3 (Codex definition): a table with state cells reads every row
  // from the top, so the row label sits on the line of its states.
  const topAlignRows = columns.some(
    (c) => c.render === "state_cell" && c.cellStyle !== "fill",
  );
  // Controlled when the ``fullscreen`` prop is provided (review #1);
  // internal state otherwise (historical behavior).
  // Is there content hidden to the RIGHT of the rows zone? Drives the
  // scroll affordance; recomputed on scroll and on resize so it is
  // never stale after a tile is dragged wider.
  const [hasHiddenRight, setHasHiddenRight] = useState(false);
  // The rows zone's VISIBLE box — the accordion expansion needs both
  // sides of it: the width to stay pinned to the viewport of a table
  // that scrolls horizontally (sticky wrapper sized to the zone, not to
  // the full table width), the height for its budget. Same observer as
  // the overflow affordance; `{0,0}` = no layout yet (jsdom / first
  // frame), never a made-up size.
  const [zoneSize, setZoneSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const measureOverflow = useCallback(() => {
    const el = rowsZoneRef.current;
    if (el === null) return;
    setHasHiddenRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    const w = el.clientWidth;
    const h = el.clientHeight;
    setZoneSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, []);
  const onRowsScroll = useCallback(() => measureOverflow(), [measureOverflow]);
  useEffect(() => {
    measureOverflow();
    const el = rowsZoneRef.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measureOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  });

  // ── Accordion expansion reveal ──
  // After the expansion mounts (and again when the host says the detail
  // settled — it grows from the skeleton to its real height then), bring
  // it into view with the LEAST movement: `block: "nearest"` on the
  // expansion, then on its row, so both end up visible without fighting
  // the reader's own scroll position. The expansion is capped so that
  // row + detail always fit in the zone (see `expansionMaxPx`), which is
  // what makes the two calls consistent. jsdom has no `scrollIntoView`
  // — the guard is a capability check, not a data fallback.
  const expansionOpen = renderRowExpansion != null && activeRowValue != null;
  useLayoutEffect(() => {
    if (!expansionOpen) return;
    const zone = rowsZoneRef.current;
    if (zone === null) return;
    const expansion = zone.querySelector<HTMLElement>("[data-row-expansion]");
    if (expansion === null) return;
    const row = expansion.previousElementSibling as HTMLElement | null;
    for (const el of [expansion, row]) {
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }
  }, [expansionOpen, activeRowValue, expansionRevealKey]);

  const [internalFullscreen, setInternalFullscreen] = useState(false);
  const isFullscreen = fullscreenProp ?? internalFullscreen;
  // Entering or leaving fullscreen changes the room the columns have, so
  // the grown widths start over (else the normal view keeps the wide ones).
  const fullscreenSeenRef = useRef(isFullscreen);
  if (fullscreenSeenRef.current !== isFullscreen) {
    fullscreenSeenRef.current = isFullscreen;
    colMinWidths.current = {};
  }
  // Same for any other change of the room: a narrower window, a grid
  // cell that settles after the first paint. The widths start over and
  // the table lays out again in the room it has now.
  const [, setRoomRevision] = useState(0);
  useEffect(() => {
    const zone = rowsZoneRef.current;
    if (!zone || typeof ResizeObserver === "undefined") return;
    let room = zone.clientWidth;
    const ro = new ResizeObserver(() => {
      const next = zone.clientWidth;
      if (Math.abs(next - room) < 1) return;
      room = next;
      colMinWidths.current = {};
      setRoomRevision((n) => n + 1);
    });
    ro.observe(zone);
    return () => ro.disconnect();
  }, []);
  const fullscreenToggleRef = useRef<HTMLButtonElement>(null);
  const setFullscreen = useCallback(
    (next: boolean) => {
      if (fullscreenProp == null) setInternalFullscreen(next);
      onFullscreenChange?.(next);
    },
    [fullscreenProp, onFullscreenChange],
  );
  // Escape leaves fullscreen and gives the focus back to the toggle (QA
  // F12, 2026-10-26: the overlay ignored Escape and kept covering the page).
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      setFullscreen(false);
      fullscreenToggleRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isFullscreen, setFullscreen]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);
  const totalPages = Math.ceil(totalCount / pageSize);

  // SWR (review #56 #6): FadeSwap keyed by rows CONTENT, not identity —
  // a refetch that lands byte-identical rows (same page, unchanged
  // data) must NOT crossfade. Signature = djb2 over the serialized
  // page, length-prefixed; cost is bounded by design (`rows` is one
  // page, ≤ pageSize = 10–40 rows → a few KB of JSON, memoized on the
  // array reference so it runs once per fetch, not per render).
  const rowsSig = useMemo(() => {
    const json = JSON.stringify(rows);
    let h = 5381;
    for (let i = 0; i < json.length; i++) {
      h = ((h << 5) + h + json.charCodeAt(i)) | 0;
    }
    return `${rows.length}:${h >>> 0}`;
  }, [rows]);
  // Render-phase key derivation (same ref pattern FadeSwap itself
  // uses). The swap key only moves between two NON-EMPTY datasets:
  // the first data landing (empty → rows) renders directly — no
  // enter animation on mount — and rows → empty swaps straight to the
  // empty state.
  const swapKeyRef = useRef(rowsSig);
  const prevSigRef = useRef(rowsSig);
  const prevHadRowsRef = useRef(rows.length > 0);
  if (prevSigRef.current !== rowsSig) {
    if (prevHadRowsRef.current && rows.length > 0) {
      swapKeyRef.current = rowsSig;
    }
    prevSigRef.current = rowsSig;
    prevHadRowsRef.current = rows.length > 0;
  }

  // ── Row cascade entrance (motion ronda 14, v2 2026-07-24) ──
  // The cascade plays on the FIRST non-empty rows landing of the
  // instance — whenever that happens. v1 coupled it to the shared
  // `useEntrance()` window (~1s after mount), so any table whose data
  // took longer (filtered fetches, cold cache) appeared dead — the
  // "sometimes yes, sometimes no" the user caught. Now the landing
  // itself arms a local zero→in flip (render-phase state, FadeSwap's
  // "adjust state during render" pattern) with the same guards:
  // reduced / ghost-suppressed / hidden document mount settled, and a
  // timeout backstop covers occluded windows. Later row sets
  // (pagination / sort / search / refetch) still mount directly at
  // the final opaque state — never re-animated.
  const cascadeReduced = usePrefersReducedMotion();
  const cascadeSuppressed = useEntranceSuppressed();
  const cascadeArmedRef = useRef(false);
  const [cascadePhase, setCascadePhase] = useState<"zero" | "in" | null>(null);
  if (!cascadeArmedRef.current && rows.length > 0) {
    cascadeArmedRef.current = true;
    if (
      !cascadeReduced &&
      !cascadeSuppressed &&
      typeof requestAnimationFrame === "function" &&
      !(typeof document !== "undefined" && document.visibilityState === "hidden")
    ) {
      setCascadePhase("zero");
    }
  }
  useEffect(() => {
    if (cascadePhase !== "zero") return undefined;
    // Double rAF: the zero state must PAINT before the transition
    // target applies. Backstop flips regardless (occluded windows
    // pause rAF silently — rows must never stay invisible).
    let raf2: number | null = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setCascadePhase("in"));
    });
    const backstop = setTimeout(() => setCascadePhase("in"), 450);
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
      clearTimeout(backstop);
    };
  }, [cascadePhase]);
  const cascadeStyle = (i: number): CSSProperties | undefined => {
    if (cascadePhase === null) return undefined;
    if (cascadePhase === "zero") {
      return { opacity: 0, transform: "translateY(6px)" };
    }
    const delay = Math.min(
      i * ROW_CASCADE_STAGGER_MS,
      ROW_CASCADE_CAP_MS - MOTION.durBase,
    );
    return {
      opacity: 1,
      transform: "none",
      transition:
        `opacity ${MOTION.durBase}ms ${MOTION.easeOut} ${delay}ms, ` +
        `transform ${MOTION.durBase}ms ${MOTION.easeOut} ${delay}ms`,
    };
  };

  // ── Phase B: per-column render computations ──────────────
  const heatRanges = useMemo<Record<string, { min: number; max: number }>>(
    () => {
      const out: Record<string, { min: number; max: number }> = {};
      for (const col of columns) {
        if (col.render !== "heat") continue;
        const values = rows
          .map((r) => Number(r[col.key]))
          .filter((n) => Number.isFinite(n));
        if (values.length > 0) {
          out[col.key] = { min: Math.min(...values), max: Math.max(...values) };
        }
      }
      return out;
    },
    [columns, rows],
  );

  // Redesign — columns in the ``trend`` render mode. The per-row
  // context (row max + bar color + parsed cells) is computed once per
  // row in the tbody map below.
  const trendCols = useMemo(
    () => columns.filter((c) => c.render === "trend"),
    [columns],
  );

  // The flagged "Total" row leaves the page order and pins at the
  // bottom. Truthiness on purpose: DuckDB emits a bool, CSV-loaded
  // facts may emit 1/"true".
  const { bodyRows, totalRow } = useMemo(() => {
    if (!totalRowKey) return { bodyRows: rows, totalRow: null };
    let found: Record<string, unknown> | null = null;
    const rest: Record<string, unknown>[] = [];
    for (const r of rows) {
      const isTotal = isTruthyFlag(r[totalRowKey]);
      if (isTotal && found == null) found = r;
      else rest.push(r);
    }
    return { bodyRows: rest, totalRow: found };
  }, [rows, totalRowKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    }
    if (exportOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [exportOpen]);

  const handleExport = useCallback(async (format: "xlsx" | "csv_latam" | "csv_us") => {
    if (rows.length === 0) return;
    setExportError(null);
    setExportNotice(null);
    if (exportAll) {
      // Server-side export: no rows pass through the browser, and no
      // fallback to the visible page when it fails.
      setExportBusy(true);
      try {
        const result = await exportAll(format);
        if (result.notice === "xlsx_as_csv") {
          setExportNotice(t("table.exportXlsxAsCsv"));
        } else {
          setExportOpen(false);
        }
      } catch (err: unknown) {
        setExportError(err instanceof Error ? err.message : String(err));
      } finally {
        setExportBusy(false);
      }
      return;
    }
    // Fail loud (owner decision): if the full set cannot be fetched, NO
    // file is written — a page-sized file named like a full export is a
    // wrong export. The raw error is shown under a translated headline.
    let exportRows: Record<string, unknown>[];
    if (fetchAllRows) {
      try {
        exportRows = await fetchAllRows();
      } catch (err: unknown) {
        setExportError(err instanceof Error ? err.message : String(err));
        return;
      }
    } else {
      exportRows = rows;
    }

    const exportColumns = columns.filter(
      (c) => !c.render || !NON_EXPORTED_RENDERS.has(c.render),
    );
    if (format === "xlsx") {
      const XLSX = await import("xlsx");
      // iter 19 — column ``label`` may arrive as a bilingual
      // ``DisplayLabel`` ``{en, es}``; resolve to the active UI locale
      // so the exported header isn't ``[object Object]``.
      const wsData = [
        exportColumns.map((c) => resolveLocalized(c.label as DisplayLabel, lang)),
        // Bilingual `{"en","es"}` cells export the text shown on screen;
        // a state token exports its label, never "down:12|0/2".
        ...exportRows.map((row) =>
          exportColumns.map((col) => {
            const v = row[col.key];
            if (col.render === "state_cell") return stateCellText(v, col, lang);
            return typeof v === "string" ? resolveLocalized(v, lang) : (v ?? "");
          }),
        ),
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      // Excel caps sheet names at 31 chars and forbids []:*?/\ .
      const sheetName =
        exportName.replace(/[[\]:*?/\\]/g, " ").trim().slice(0, 31) || t("table.exportSheet");
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `${exportName}.xlsx`);
    } else {
      const sep = format === "csv_latam" ? ";" : ",";
      const suffix = format === "csv_latam" ? "_latam" : "_us";
      // iter 19 — resolve bilingual ``label`` to active locale (was raw).
      const header = exportColumns
        .map((c) => csvField(resolveLocalized(c.label as DisplayLabel, lang), sep))
        .join(sep);
      const csvRows = exportRows.map((row) =>
        exportColumns
          .map((col) => {
            const val = row[col.key];
            if (col.render === "state_cell") return csvField(stateCellText(val, col, lang), sep);
            if (val === null || val === undefined) return "";
            const str = typeof val === "string" ? resolveLocalized(val, lang) : String(val);
            if (format === "csv_latam") {
              const num = Number(val);
              if (!isNaN(num) && str === String(num)) return str.replace(".", ",");
            }
            return csvField(str, sep);
          })
          .join(sep)
      );
      const csv = [header, ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${exportName}${suffix}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    }
    setExportOpen(false);
  }, [rows, fetchAllRows, exportAll, columns, exportName, lang, t]);

  // Flex-column mode: fullscreen (historical), fill (grid cell) or cap
  // (mobile stack). In all three the row zone scrolls internally and
  // the pagination footer stays pinned; outside them the card keeps
  // its intrinsic-height layout byte-for-byte.
  const flexMode = isFullscreen || fillHeight || capPx != null;
  const containerClass = isFullscreen
    ? "fixed inset-0 z-50 flex flex-col bg-white overflow-hidden"
    : `rounded-[12px] border border-neutral-200 bg-white shadow-card overflow-hidden${
        fillHeight ? " flex h-full flex-col" : capPx != null ? " flex flex-col" : ""
      }${className ? ` ${className}` : ""}`;

  return (
    <div
      data-table-surface=""
      data-render-table=""
      data-density="compact"
      data-fullscreen={isFullscreen || undefined}
      className={containerClass}
      style={
        !isFullscreen && capPx != null ? { maxHeight: capPx } : undefined
      }
    >
      {/* Section header — compact single-line variant (table cards). */}
      <SectionHeader
        compact
        title={titleProp ?? t("table.title")}
        info={info}
        subtitle={subtitleProp ?? t("table.count", { count: totalCount })}
        rightContent={
          <div className="flex items-center gap-2">
            {/* Search input */}
            {onSearchChange && (
              <div className="relative">
                <Search size={13} aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
                <input
                  type="text"
                  value={search ?? ""}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder ?? t("table.search")}
                  className="w-44 rounded-[10px] border border-control bg-white pl-8 pr-3 py-1.5 text-xs text-neutral-800 placeholder:text-neutral-500 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                />
              </div>
            )}
            {/* Export dropdown */}
            <div className="relative" ref={exportRef}>
              <button
                onClick={() => {
                  setExportError(null);
                  setExportOpen((prev) => !prev);
                }}
                className="inline-flex items-center gap-1.5 rounded-[9px] border border-control bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-primary-soft hover:text-primary-ink transition-colors"
              >
                <Download size={13} />
                {exportBusy ? t("table.exporting") : t("common.export")}
                <ChevronDown size={12} />
              </button>
              {exportOpen && (
                <div data-dropdown-surface="menu" className="absolute right-0 mt-1 w-48 rounded-xl border border-neutral-200 bg-white p-1 shadow-menu z-20 overflow-hidden">
                  <button
                    onClick={() => handleExport("xlsx")}
                    disabled={exportBusy}
                    className="w-full rounded-[7px] px-3 py-2 text-[13px] text-left text-neutral-700 hover:bg-primary-soft cursor-pointer disabled:cursor-wait disabled:opacity-50"
                  >
                    {t("table.exportXlsx")}
                  </button>
                  <button
                    onClick={() => handleExport("csv_latam")}
                    disabled={exportBusy}
                    className="w-full rounded-[7px] px-3 py-2 text-[13px] text-left text-neutral-700 hover:bg-primary-soft cursor-pointer disabled:cursor-wait disabled:opacity-50"
                  >
                    {t("table.exportCsvLatam")}
                  </button>
                  <button
                    onClick={() => handleExport("csv_us")}
                    disabled={exportBusy}
                    className="w-full rounded-[7px] px-3 py-2 text-[13px] text-left text-neutral-700 hover:bg-primary-soft cursor-pointer disabled:cursor-wait disabled:opacity-50"
                  >
                    {t("table.exportCsvUs")}
                  </button>
                  {exportNotice !== null && (
                    <div
                      role="status"
                      className="border-t border-neutral-200 px-4 py-2 text-xs text-neutral-700"
                    >
                      {exportNotice}
                    </div>
                  )}
                  {exportError !== null && (
                    <div
                      role="alert"
                      className="border-t border-neutral-200 px-3 py-2 text-xs text-error-dark"
                    >
                      <p className="font-semibold">{t("table.exportFailed")}</p>
                      <p className="mt-0.5 break-words">{exportError}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              ref={fullscreenToggleRef}
              type="button"
              onClick={() => setFullscreen(!isFullscreen)}
              className="inline-flex items-center gap-1.5 rounded-[9px] border border-control bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-primary-soft hover:text-primary-ink transition-colors"
            >
              {isFullscreen ? <X size={13} /> : <Maximize2 size={13} />}
              {isFullscreen ? t("table.exitFullscreen") : t("table.fullscreen")}
            </button>
          </div>
        }
      />

      {/* Quick-filter + sort chips (DSA prototype v7 matrix): a light
          strip under the dark header, one segmented group each. */}
      {(quickFilters || sortPresets) && (
        <div
          data-table-chips
          className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-neutral-200 bg-white px-4 py-2"
        >
          {quickFilters && (
            <div role="group" aria-label={t("table.chips.filter")} className="flex flex-wrap gap-1">
              {quickFilters.options.map((o) => {
                const on = quickFilters.active === o.value;
                return (
                  <button
                    key={o.value || "__all"}
                    type="button"
                    aria-pressed={on}
                    onClick={() => quickFilters.onChange(o.value)}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${
                      on
                        ? "border-primary-dark bg-primary-dark text-white"
                        : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}
          {sortPresets && (
            <div role="group" aria-label={t("table.chips.sort")} className="flex flex-wrap gap-1">
              {sortPresets.options.map((o) => {
                const on =
                  sortPresets.activeSortBy === o.sortBy && sortPresets.activeSortDir === o.sortDir;
                return (
                  <button
                    key={`${o.sortBy}:${o.sortDir}`}
                    type="button"
                    aria-pressed={on}
                    onClick={() => sortPresets.onSelect(o.sortBy, o.sortDir)}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${
                      on
                        ? "border-neutral-700 bg-neutral-700 text-white"
                        : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Table — compact: text-[11px], tight padding, truncate for text
          columns. In flex mode the zone claims the leftover height and
          scrolls internally — the sticky thead pins to its top. */}
      <div
        // Stable hook for the host (review #16): DashboardTable captures
        // this zone's scrollTop before the row-drill swap hides the face
        // (display:none discards it) and restores it on "Back".
        data-rows-zone=""
        ref={rowsZoneRef}
        onScroll={onRowsScroll}
        className={
          (flexMode ? "flex-1 min-h-0 overflow-y-auto overflow-x-auto" : "flex-1 overflow-x-auto") +
          // Horizontal-scroll affordance. A 13-column table inside a
          // 632px board tile hides ~1400px to the right, and the ONLY
          // hint was a scrollbar that macOS does not draw until you
          // gesture — so the columns were, for practical purposes,
          // invisible. The fade says "there is more", and it is masked
          // out once you reach the end so it never lies.
          (hasHiddenRight && !isDrillTable ? " [mask-image:linear-gradient(to_right,black_calc(100%-40px),transparent)]" : "")
        }
        // Grid-cell mode: never let the rows zone collapse below ~2
        // rows — an absurdly small cell overflows visibly (review #4).
        style={fillHeight ? { minHeight: MIN_ROWS_ZONE_PX } : undefined}
      >
        {/* Whole-table crossfade (decision, review #56 #6): the
            outgoing face re-renders the previous <table> (thead
            included) for ~180ms. Kept as-is instead of a tbody-only
            fade because (a) the clone is bounded by pageSize (≤ 40
            rows), and (b) FadeSwap wraps faces in <div>s — divs are
            valid around a <table> but NOT inside one, so a tbody-only
            crossfade would emit invalid HTML. */}
        <FadeSwap swapKey={swapKeyRef.current}>
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-neutral-50 border-b border-neutral-200">
            <tr>
              {columns.map((col, colIdx) => (
                <th
                  key={col.key}
                  ref={(el) => {
                    headRefs.current[colIdx] = el;
                  }}
                  scope="col"
                  // QA F07: the sort lives in a real <button> (keyboard,
                  // focus ring) and the header states it with aria-sort.
                  aria-sort={sortBy === col.key ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  className={`whitespace-nowrap px-3 py-[13px] first:pl-5 last:pr-5 ${
                    col.render === "trend" || isRightAlignedColumn(col)
                      ? "text-right"
                      : "text-left"
                  } text-2xs font-semibold text-neutral-500 select-none${
                    colIdx < stickyCount
                      ? // El `bg-` del <thead> pinta la FILA, no la celda,
                        // asi que una celda sticky se ve translucida sobre
                        // las que le pasan por debajo. Y la ultima
                        // congelada lleva el borde que marca donde
                        // termina la zona fija.
                        " bg-neutral-50" +
                        (colIdx === stickyCount - 1
                          ? " border-r border-neutral-300"
                          : "")
                      : ""
                  }`}
                  // Mirror of the table-chart header offset: trend cells
                  // reserve 66px chip zone + 5px gap right of the value,
                  // so the header right edge shifts by 6px td px-1.5 + 71.
                  // El sticky se SUMA: una columna congelada tambien
                  // puede ser una columna trend.
                  style={{
                    ...(colMinWidths.current[col.key] ? { minWidth: colMinWidths.current[col.key] } : {}),
                    ...(col.render === "trend" ? { paddingRight: 77 } : {}),
                    ...(col.maxWidth === undefined
                      ? {}
                      : { maxWidth: col.maxWidth, overflow: "hidden", textOverflow: "ellipsis" }),
                    ...stickyStyle(colIdx),
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onSort(col.key)}
                    className="inline-flex items-center gap-0.5 rounded-md font-semibold text-inherit hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {resolveLocalized(col.label as DisplayLabel, lang)}
                    {sortBy === col.key &&
                      (sortDir === "asc" ? (
                        <ChevronUp size={12} aria-hidden="true" />
                      ) : (
                        <ChevronDown size={12} aria-hidden="true" />
                      ))}
                  </button>
                  {/* Formula line (`sub_label`): which denominator this
                      column uses, in the header itself — the reader
                      should not need a tooltip to know what 70 % is of. */}
                  {col.subLabel != null &&
                    resolveLocalized(col.subLabel, lang) !== "" && (
                      <div className="text-2xs font-normal leading-tight text-neutral-500">
                        {resolveLocalized(col.subLabel, lang)}
                      </div>
                    )}
                </th>
              ))}
              {markColumn && (
                <th
                  className={`w-px px-3 py-1.5 bg-neutral-50 ${markStickyClass}`}
                  aria-label={t("tasks.mark.column")}
                />
              )}
              {/* Fondo propio y opaco: el gris del encabezado lo pinta el
                  <thead>, no la fila, así que `bg-inherit` quedaba
                  transparente y los títulos de las columnas que pasan por
                  debajo se leían a través ("Ver detalle" sobre
                  "Mercadolibre", 2026-10-26). La sombra a la izquierda dice
                  que la columna flota sobre las que pasan por debajo. */}
              {isDrillTable && <th data-row-action-column="" scope="col" className={`sticky right-0 z-[4] ${detailWidthClass} border-l border-neutral-200 bg-neutral-50 shadow-[-6px_0_8px_-6px_theme(colors.neutral.400)] px-3 py-[13px] text-center text-2xs font-semibold text-neutral-500 max-sm:px-1`}><span className="max-sm:sr-only">{t("table.rowdrill.open")}</span><span aria-hidden="true" className="hidden max-sm:inline">···</span></th>}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, idx) => {
              const rowDrillVal =
                onRowClick && rowDrillKey
                  ? String(row[rowDrillKey] ?? "")
                  : null;
              const isActive =
                activeRowValue != null && rowDrillVal === activeRowValue;
              const highlightRaw = highlightRowKey ? row[highlightRowKey] : undefined;
              // A tone value tints with that tone; any other truthy flag
              // tints neutral (both opaque: sticky cells inherit it).
              const highlightTone =
                typeof highlightRaw === "string" &&
                (TONES as ReadonlyArray<string>).includes(highlightRaw) &&
                highlightRaw !== "neutral"
                  ? (highlightRaw as Tone)
                  : null;
              const isHighlighted = highlightTone == null && isTruthyFlag(highlightRaw);
              const rowTrend =
                trendCols.length > 0
                  ? buildRowTrendContext(row, columns, trendCols, lang)
                  : null;
              return (
              <Fragment key={idx}>
              <tr
                data-row-tone={highlightTone ?? undefined}
                style={
                  highlightTone && !isActive
                    ? { ...cascadeStyle(idx), background: toneStyle(highlightTone).bg }
                    : cascadeStyle(idx)
                }
                // UX affordance: drillable rows advertise themselves — a
                // native tooltip plus the dedicated action rendered in the last
                // cell below. ``group`` also lights up the filter glyphs
                // of cross-filterable cells on row hover.
                title={
                  rowDrillVal != null ? t("table.rowdrill.hint") : undefined
                }
                className={`group border-b border-neutral-100 transition-colors duration-150 cursor-pointer hover:bg-primary-soft [&>td:first-child]:font-semibold [&>td:first-child]:text-neutral-800 ${
                  isActive
                    ? "bg-primary-soft shadow-[inset_2px_0_0_0_var(--omni-violet)]"
                    : // El cebrado NO puede llevar alfa. `bg-neutral-50/50`
                      // era neutral-50 al 50%, y una celda congelada toma
                      // el fondo de su fila con `bg-inherit`: heredaba un
                      // fondo semitransparente y las columnas que le
                      // pasaban por debajo se LEIAN A TRAVES (reportado en
                      // stg: "LAVADORAS_SECADORAS" cruzando MODELO). Solo
                      // en las filas alternas, que es lo que hace el bug
                      // facil de perder de vista. Ahora es neutral-50 del
                      // kit, opaco (QA F15: el #FDFDFD fijo no era un
                      // token v3).
                      // Opaque too, for the same reason (sticky cells
                      // inherit it).
                      highlightTone
                      ? ""
                      : isHighlighted
                      ? "bg-neutral-100"
                      : idx % 2 === 0
                        ? "bg-white"
                        : "bg-neutral-50"
                }`}
                onClick={
                  rowDrillVal != null
                    ? () => onRowClick!(rowDrillVal, row)
                    : undefined
                }
              >
                {columns.map((col, colIdx) => {
                  const raw = row[col.key];
                  const render = col.render ?? "default";
                  const heatStyle =
                    render === "heat"
                      ? heatStyleFor(raw, heatRanges[col.key])
                      : undefined;
                  // Cross-filter: only scalar, non-empty, non-URL,
                  // non-bilingual-JSON cells are clickable. URL cells
                  // already render their own click-out link; objects /
                  // arrays (sparklines) have no meaningful filter value.
                  // An `action` cell IS the filter control (a button);
                  // never wrap it in the underline cross-filter too.
                  const cellFilter =
                    col.render !== "action" &&
                    col.filterKey &&
                    onCellFilter &&
                    raw != null &&
                    raw !== "" &&
                    typeof raw !== "object" &&
                    !(typeof raw === "string" && /^https?:\/\//i.test(raw)) &&
                    !isBilingualJsonString(raw)
                      ? { key: col.filterKey, apply: onCellFilter }
                      : null;
                  // A multi_select target holds ``["<value>"]``: compare
                  // through the shared reader so the underline lights up.
                  const isActiveCellFilter =
                    cellFilter != null &&
                    crossFilterActiveValue(activeFilters?.[cellFilter.key]) === String(raw);
                  // Value-based alignment (UI polish 2026-07): numeric
                  // columns and ISO-date cells read right-aligned with
                  // tabular-nums; date cells are additionally muted in
                  // the renderer below.
                  const isDateCell = isIsoDateString(raw);
                  const rightAligned = isRightAlignedColumn(col) || isDateCell;
                  // The drill-trigger column is "the actionable value":
                  // accent text, medium weight — no permanent underline.
                  const isTriggerCell =
                    isDrillTable &&
                    rowDrillVal != null &&
                    (col.key === rowDrillKey || col.label === rowDrillKey);
                  // Resolved here (not inside `renderCellByType`) so the
                  // subtext sits OUTSIDE the cross-filter button: the
                  // clickable target is the figure, never its
                  // denominator.
                  const subText = col.subKey
                    ? resolveLocalized(row[col.subKey] as DisplayLabel, lang)
                    : "";
                  const badgeText = col.badgeKey
                    ? resolveLocalized(row[col.badgeKey] as DisplayLabel, lang)
                    : "";
                  // Mixed columns read OTHER keys of the row (denominator,
                  // stack parts, the action's target); resolved here, not
                  // inside `renderCellByType`, which only sees the cell.
                  const mixed = renderMixedCell(raw, col, row, {
                    lang,
                    t,
                    subText,
                    onCellFilter,
                    activeFilters,
                  });
                  return (
                    <td
                      key={col.key}
                      data-cell-render={render}
                      data-table-value=""
                      className={`px-3 first:pl-5 last:pr-5 ${
                        // Trend cells are two text-lines tall (value +
                        // micro-bar) — a touch more vertical padding so
                        // consecutive rows don't read cramped. The 66px
                        // chip zone / column alignment is untouched.
                        col.render === "trend" ? "py-[11px]" : "py-[13px]"
                      } ${
                        isTriggerCell
                          ? "font-semibold text-neutral-800"
                          : "text-neutral-700"
                      } tabular-nums ${
                        col.truncate ? col.truncate : "whitespace-nowrap"
                      }${col.render === "trend" ? " text-right align-top" : ""}${
                        rightAligned && col.render !== "trend"
                          ? " text-right"
                          : ""
                      }${
                        colIdx < stickyCount
                          ? // Congelada: `bg-inherit` toma el cebrado de la
                            // fila (blanco / neutral-50) en vez de dejar
                            // pasar las celdas de al lado.
                            " bg-inherit" +
                            (colIdx === stickyCount - 1
                              ? " border-r border-neutral-200"
                              : "")
                          : ""
                      }`}
                      style={{
                        // State badges share a baseline even when only some
                        // states include secondary lines (e.g. presence yes/no).
                        ...(topAlignRows ? { verticalAlign: "top" } : {}),
                        ...heatStyle,
                        ...(col.maxWidth === undefined
                          ? {}
                          : {
                              maxWidth: col.maxWidth,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }),
                        ...stickyStyle(colIdx),
                      }}
                      // Cell content already runs through resolveLocalized
                      // inside renderCellByType, but the browser-native
                      // truncate tooltip reads the raw value — wrap it
                      // here too so bilingual JSON literals never leak.
                      title={
                        col.truncate || col.maxWidth !== undefined
                          ? resolveLocalized(raw as DisplayLabel, lang) ||
                            undefined
                          : undefined
                      }
                    >
                      {cellFilter ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            // Keep the row-drill (and any other row-level
                            // gesture) untouched — the cell click only
                            // toggles the report filter.
                            e.stopPropagation();
                            cellFilter.apply(cellFilter.key, String(raw));
                          }}
                          title={t(
                            isActiveCellFilter
                              ? "table.crossfilter.clear"
                              : "table.crossfilter.apply",
                          )}
                          // Anti-jump (§2.5): the active state changes
                          // COLOR + decoration style only — never
                          // font-weight, which alters the text width and
                          // re-flows the column when a cell filter
                          // toggles.
                          className={`underline underline-offset-2 hover:text-accent-dark cursor-pointer ${
                            isActiveCellFilter
                              ? "text-accent-dark decoration-solid decoration-2"
                              : "decoration-dotted"
                          }`}
                        >
                          {mixed ?? renderCellByType(raw, col, linkLabel, lang, rowTrend)}
                          {/* UX affordance: a subtle always-on glyph marks
                              the cell as filterable; it turns accent on
                              row hover (tr carries ``group``). */}
                          <Filter
                            size={10}
                            aria-hidden="true"
                            className={`ml-1 inline-block align-[-1px] transition-colors ${
                              isActiveCellFilter
                                ? "text-accent-dark"
                                : "text-neutral-300 group-hover:text-accent-dark"
                            }`}
                          />
                        </button>
                      ) : (
                        mixed ?? renderCellByType(raw, col, linkLabel, lang, rowTrend)
                      )}
                      {badgeText !== "" && (
                        <span className="ml-1.5 inline-flex items-center rounded-full border border-neutral-300 bg-white px-1.5 py-px align-[1px] text-2xs font-bold uppercase tracking-wide text-neutral-600">
                          {badgeText}
                        </span>
                      )}
                      {/* Subtext (`sub_key`): the denominator the figure
                          above is measured against, on its own muted
                          line INSIDE the same cell. An empty or absent
                          value renders nothing — a row with no
                          denominator is a legitimate row, not an error,
                          and the column keeps its baseline either way. */}
                      {subText !== "" && col.render !== "fraction" && (
                        <div className="mt-0.5 text-2xs font-normal leading-tight text-neutral-500">
                          {subText}
                        </div>
                      )}
                    </td>
                  );
                })}
                {markColumn && (
                  <td
                    data-row-mark-cell=""
                    className={`w-px whitespace-nowrap px-3 py-[7px] text-center bg-inherit ${markStickyClass}`}
                    // The mark is its own control: a click on it must not
                    // open (or close) the row drill underneath.
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderRowMark!(row)}
                  </td>
                )}
                {isDrillTable && <td data-row-action-column="" className={`sticky right-0 z-[3] ${detailWidthClass} border-l border-neutral-200 bg-inherit shadow-[-6px_0_8px_-6px_theme(colors.neutral.400)] px-3 py-[9px] text-center align-top max-sm:px-1`}>
                  <div className="flex items-center justify-center gap-1.5">
                  {markInDetail && (
                    // The flag is its own control: a click on it must not
                    // open (or close) the row drill underneath.
                    <span data-row-mark-cell="" className="contents" onClick={(e) => e.stopPropagation()}>
                      {renderRowMark!(row)}
                    </span>
                  )}
                  {rowDrillVal != null && <button type="button" data-row-detail-action="" aria-label={`${t(isActive ? "table.rowdrill.close" : "table.rowdrill.open")}: ${rowDrillVal}`} aria-expanded={isActive} onClick={(event) => {event.stopPropagation(); onRowClick!(rowDrillVal, row);}} className="inline-flex min-h-7 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-control bg-white px-2 py-[5px] text-2xs font-semibold leading-4 text-primary transition-colors hover:border-primary hover:bg-primary-soft group-hover:border-primary group-hover:bg-primary-soft aria-expanded:bg-primary-soft aria-expanded:text-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary max-sm:min-h-10 max-sm:min-w-10 max-sm:p-2">
                    {isActive ? <ChevronUp size={13} aria-hidden="true"/> : <ChevronDown size={13} aria-hidden="true"/>}
                    <span data-detail-label="" className="max-sm:sr-only">{t(isActive ? "table.rowdrill.close" : "table.rowdrill.open")}</span>
                  </button>}
                  </div>
                </td>}
              </tr>
              {/* ── Accordion expansion (row_drill_mode "accordion") ──
                  The detail row lives HERE, inside the scrolling rows
                  zone, right under the active row: the card and the
                  grid cell keep their size (the invariant), the row
                  stays visible above its detail, and the zone scrolls.
                  The inner wrapper is `position: sticky; left: 0` and
                  sized to the zone's VISIBLE width: in a table wider
                  than its zone the <td> spans the whole table, so
                  without this the detail would slide away with the
                  columns on horizontal scroll (and sticky on the <td>
                  itself does nothing — a cell as wide as its row has
                  nowhere to stick). */}
              {isActive && renderRowExpansion && (
                <tr data-row-expansion="" data-row-value={rowDrillVal ?? undefined}>
                  <td colSpan={fullSpan} className="p-0 align-top">
                    <div
                      data-row-expansion-body=""
                      // Delimitado por ARRIBA y por abajo, no solo
                      // abajo: con un unico borde inferior el detalle
                      // se fundia con la fila que lo abrio y se leia
                      // como mas filas de la tabla en vez de como otra
                      // cosa (feedback de Pablo contra el reporte de
                      // referencia, que lo enmarca). El acento lateral
                      // dice de QUE fila cuelga sin depender del color:
                      // es una barra, una forma.
                      className="border-y border-neutral-200 bg-neutral-50 shadow-[inset_2px_0_0_0_var(--omni-violet)]"
                      style={{
                        position: "sticky",
                        left: 0,
                        ...(zoneSize.w > 0 ? { width: zoneSize.w } : {}),
                        maxWidth: "100%",
                      }}
                    >
                      {renderRowExpansion({ maxPx: expansionMaxPx(zoneSize.h) })}
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
            {totalRow != null && (
              // Pinned "Total" row: bold, its own top rule, outside the
              // zebra and the row drill. Cells render with the same
              // renderers as the rows above (a fraction totals as a
              // fraction, a mix bar as a mix bar); the action cell is
              // the one thing a total has no business doing.
              <tr
                data-total-row=""
                className="border-t border-neutral-300 bg-neutral-50 font-semibold text-neutral-800"
              >
                {columns.map((col, colIdx) => {
                  const raw = totalRow[col.key];
                  const rightAligned =
                    isRightAlignedColumn(col) || isIsoDateString(raw);
                  const subText = col.subKey
                    ? resolveLocalized(totalRow[col.subKey] as DisplayLabel, lang)
                    : "";
                  const mixed =
                    col.render === "action"
                      ? <span aria-hidden="true" />
                      : renderMixedCell(raw, col, totalRow, {
                          lang,
                          t,
                          subText,
                          onCellFilter: undefined,
                          activeFilters: undefined,
                        });
                  return (
                    <td
                      key={col.key}
                      className={`px-3 py-[13px] first:pl-5 last:pr-5 tabular-nums whitespace-nowrap${
                        rightAligned ? " text-right" : ""
                      }${colIdx < stickyCount ? " bg-inherit" : ""}`}
                      style={stickyStyle(colIdx)}
                    >
                      {colIdx === 0 && (raw == null || raw === "")
                        ? t("table.total")
                        : mixed ?? renderCellByType(raw, col, linkLabel, lang, null)}
                      {subText !== "" && col.render !== "fraction" && (
                        <div className="mt-0.5 text-2xs font-normal leading-tight text-neutral-500">
                          {subText}
                        </div>
                      )}
                    </td>
                  );
                })}
                {markColumn && <td aria-hidden="true" className={`bg-inherit ${markStickyClass}`} />}
                {isDrillTable && <td data-row-action-column="" className="sticky right-0 border-l border-neutral-200 bg-inherit shadow-[-6px_0_8px_-6px_theme(colors.neutral.400)]" />}
              </tr>
            )}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={fullSpan}
                  className="px-4 py-8 text-center text-neutral-500"
                >
                  {loading ? t("common.loadingData") : t("common.noData")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </FadeSwap>
      </div>

      {/* Pagination — pinned to the card bottom in flex mode. */}
      <div data-table-pagination="" className="flex shrink-0 flex-wrap gap-3 items-center justify-between border-t border-neutral-200 bg-white px-5 py-3">
        <span className="text-xs text-neutral-500 tabular-nums">
          {totalCount > 0
            ? t("dashboard.table.showing", { from, to, total: totalCount })
            : ""}
        </span>
        {/* One page: nothing to page through, so no Previous / 1 of 1 /
            Next (critique of Disponibilidad, 2026-10-27: an 8-row table
            carried a pager that paged nothing). The count stays. */}
        {totalPages > 1 && (
        <div data-table-pager="" className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="rounded-[9px] border border-control bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-primary-soft hover:text-primary-ink disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t("dashboard.table.previous")}
          </button>
          <span className="text-xs text-neutral-500 tabular-nums">
            {page} / {totalPages || 1}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="rounded-[9px] border border-control bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-primary-soft hover:text-primary-ink disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t("dashboard.table.next")}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// ── Phase B — cell render dispatchers (mirror of builder version) ──

/** Per-row context for ``trend`` columns: parsed cells, the row max
 *  (micro-bar scale) and the bar color derived from the metric
 *  (first) column's emoji-dot prefix. */
interface RowTrendContext {
  max: number;
  color: string;
  parsed: Record<string, ParsedValueDelta | null>;
}

function buildRowTrendContext(
  row: Record<string, unknown>,
  columns: ColumnDef[],
  trendCols: ColumnDef[],
  lang: Lang,
): RowTrendContext {
  const parsed: Record<string, ParsedValueDelta | null> = {};
  let max = 0;
  for (const c of trendCols) {
    const raw = row[c.key];
    const p = typeof raw === "string" ? parseValueDelta(raw) : null;
    parsed[c.key] = p;
    if (p && Number.isFinite(p.value)) max = Math.max(max, Math.abs(p.value));
  }
  // Bar color: deterministic emoji-dot mapping on the row's metric
  // (first visible column) label — resolved to the active locale
  // first, since gating_executive emits bilingual JSON there.
  const metricCol = columns[0];
  const metricLabel = metricCol
    ? resolveLocalized(row[metricCol.key] as DisplayLabel, lang)
    : "";
  return { max, color: trendMetricColor(metricLabel), parsed };
}

/** The band in words, for screen readers (a red 72 % and a green 94 %
 *  are otherwise the same to them). This function has the language but
 *  no `t`, so the four words live here. */
const TONE_WORDS: Record<Lang, Record<Tone, string>> = {
  en: { good: "good", warn: "warn", bad: "bad", info: "info", info_light: "info", neutral: "neutral", muted: "no data" },
  es: { good: "en meta", warn: "atención", bad: "en riesgo", info: "información", info_light: "información", neutral: "sin banda", muted: "sin dato" },
  pt: { good: "na meta", warn: "atenção", bad: "em risco", info: "informação", info_light: "informação", neutral: "sem faixa", muted: "sem dado" },
};

function renderCellByType(
  raw: unknown,
  col: ColumnDef,
  linkLabel: string,
  lang: Lang = "en",
  rowTrend: RowTrendContext | null = null,
): ReactNode {
  const render = col.render ?? "default";
  switch (render) {
    case "sparkline": {
      // Points are bare numbers, or `{d|label|date, v|value}` objects
      // (DuckDB `list(struct_pack(d := day, v := value) ORDER BY day)`)
      // so the hover can name the day of each point.
      const series = parseSparklineSeries(raw);
      if (!series || series.values.length < 2) {
        return <span className="text-neutral-400">—</span>;
      }
      return (
        <Sparkline
          data={series.values}
          labels={series.labels ?? undefined}
          format={(v) => formatCellValue(v, col.format)}
          width={120}
          height={28}
        />
      );
    }
    case "chip": {
      // An entity, not a measurement: a retailer name reads as a label
      // the row IS, and the pill says so. Deliberately ONE neutral
      // colour — a per-value palette would be a colour scale nobody
      // authored, and on a red-green colourblind owner an invented
      // scale is worse than none. Bilingual JSON still resolves.
      const text = resolveLocalized(raw as DisplayLabel, lang);
      if (text === "" || raw == null) {
        return <span className="text-neutral-300">—</span>;
      }
      return (
        <span className="inline-flex items-center rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700">
          {text}
        </span>
      );
    }
    case "tone": {
      // The threshold semaphore, delivered on the EXISTING per-column
      // channel rather than a new component — the sanctioned path for
      // conditional cell styling in this codebase (see the note in
      // `renderCellDefault`: opt-in via report config, NEVER value
      // detection). The bands are authored; nothing is inferred.
      const num = typeof raw === "number" ? raw : Number(raw);
      if (!col.thresholds || col.thresholds.length === 0) {
        // Fail visibly rather than tinting neutral: an author who asked
        // for a semaphore and got plain grey has no way to know.
        return (
          <span className="text-danger-600" title="render:'tone' requires thresholds">
            ⚠ sin bandas
          </span>
        );
      }
      // NULL is "no value" (a retailer without stock reading has no
      // availability), never 0: Number(null) would paint it "bad".
      if (raw == null || raw === "" || !Number.isFinite(num)) {
        return <span className="text-neutral-300">—</span>;
      }
      const tone = resolveTone({ thresholds: col.thresholds }, num);
      const palette = toneStyle(tone);
      return (
        <span
          className="inline-flex items-center rounded-md px-1.5 py-0.5 font-bold tabular-nums"
          style={{ color: palette.fg, background: palette.bg }}
        >
          {formatCellValue(raw, col.format)}
          {/* The band as text: a red 72% and a green 94% are otherwise
              indistinguishable to a colourblind or screen-reader user. */}
          <span className="sr-only"> — {TONE_WORDS[lang][tone]}</span>
        </span>
      );
    }
    case "status": {
      const label = String(raw ?? "").trim();
      if (!label) return <span className="text-neutral-400">—</span>;
      // Substring match — SQL values like "Active SKU" / "Inactive
      // Seller" / "Warning — low stock" should map without coercing.
      // Check `inactive` before `active` since the former contains it.
      const lower = label.toLowerCase();
      const cls = lower.includes("inactive")
        ? "bg-neutral-100 text-neutral-600 ring-neutral-200"
        : lower.includes("critical") || lower.includes("error") || lower.includes("fail")
          ? "bg-red-50 text-negative ring-red-200"
          : lower.includes("warning") || lower.includes("alert") || lower.includes("risk")
            ? "bg-amber-50 text-caution ring-amber-200"
            : lower.includes("pending") || lower.includes("queue") || lower.includes("review")
              ? "bg-blue-50 text-blue-700 ring-blue-200"
              : lower.includes("active") || lower.includes("ok") || lower.includes("listed")
                ? "bg-emerald-50 text-positive ring-emerald-200"
                : "bg-neutral-100 text-neutral-700 ring-neutral-200";
      return (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-semibold ring-1 ${cls}`}
        >
          {label}
        </span>
      );
    }
    case "progress": {
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        return <span className="text-neutral-400">—</span>;
      }
      const pct = Math.max(0, Math.min(100, num));
      // Redesign v2: progress renders in the brand magenta with the
      // bold value always visible — completion is not a good/bad
      // signal, so no traffic-light coloring.
      return (
        <span className="inline-flex items-center gap-2 align-middle">
          <span className="relative inline-block h-1.5 w-24 overflow-hidden rounded-full bg-neutral-200 align-middle">
            <span
              className="absolute left-0 top-0 h-full rounded-full"
              style={{ width: `${pct}%`, background: PALETTE[0] }}
            />
          </span>
          <span className="text-[11px] font-bold text-neutral-800 tabular-nums">
            {pct.toFixed(0)}%
          </span>
        </span>
      );
    }
    case "compare": {
      const num = Number(raw);
      if (!Number.isFinite(num) || num === 0) {
        return (
          <span className="text-neutral-500">
            {Number.isFinite(num) ? "0%" : "—"}
          </span>
        );
      }
      const label =
        col.format === "percent"
          ? `${num > 0 ? "+" : ""}${(num * 100).toFixed(1)}%`
          : `${num > 0 ? "+" : ""}${num.toFixed(1)}%`;
      // Redesign rule 4: semantic ▲/▼ pastel delta chip.
      return <DeltaChip delta={num} label={label} className="text-2xs" />;
    }
    case "heat":
      return <span>{formatCellValue(raw, col.format)}</span>;
    case "trend": {
      // Redesign — rich comparison cell for SQL-formatted
      // "value ▲/▼ ±x.x%" strings (gating_executive fiscal table).
      // STRICT: a cell that didn't parse renders EXACTLY like the
      // default mode — degradation is style-only, never data.
      const parsed = rowTrend?.parsed[col.key] ?? null;
      if (!rowTrend || !parsed) {
        return renderDefaultCell(raw, col, linkLabel, lang);
      }
      return (
        <TrendCell parsed={parsed} max={rowTrend.max} color={rowTrend.color} />
      );
    }
    case "default":
    default:
      return renderDefaultCell(raw, col, linkLabel, lang);
  }
}

// ── Mixed columns (DSA 1.19) ─────────────────────────────────
// Cells that read OTHER keys of the row: the denominator of a fraction,
// the parts of a mix bar, the value an action button filters by. They
// take the whole row, so they live outside `renderCellByType` (which
// only ever sees the cell) and return null for every other render
// mode — the caller falls through to the per-cell dispatcher.

interface MixedCellCtx {
  lang: Lang;
  t: (key: string, params?: Record<string, unknown>) => string;
  /** The `sub_key` text — a fraction folds it into its share line. */
  subText: string;
  onCellFilter?: (filterKey: string, rawValue: string) => void;
  activeFilters?: Record<string, string>;
}

/** Colours of the parts of a ``mini_stack`` that are categories (no
 *  tone): the series palette, counting only those parts. */
const STACK_SERIES_COLORS = [PALETTE[0], PALETTE[1], PALETTE[2], PALETTE[3]];

/** A row flag column (total / highlight). Truthiness on purpose: DuckDB
 *  emits a bool, CSV-loaded facts may emit 1 / "true". */
function isTruthyFlag(flag: unknown): boolean {
  return flag === true || flag === 1 || flag === "true" || flag === "1";
}

/** Default glyph per tone for `state_cell` (a shape, so the state is
 *  legible without colour); a state's own `glyph` overrides it. `bad`
 *  is ✕ since the look v3 (2026-10-24, the approved "✕ No"); a state
 *  that means "went down" declares its own ▼. */
const STATE_GLYPH_BY_TONE: Record<Tone, string> = {
  good: "✓",
  warn: "◐",
  bad: "✕",
  info: "i",
  info_light: "i",
  neutral: "—",
  muted: "·",
};

// Counts and shares in the APP language ("1.062" / "70,5 %" in ES), not
// the browser's: a Spanish UI in an English browser printed "1,062".
function formatCount(n: number): string {
  return Number.isInteger(n)
    ? formatNumberLocale(n)
    : formatNumberLocale(n, { maximumFractionDigits: 1 });
}

function formatFixed(n: number, digits: number): string {
  return formatNumberLocale(n, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

interface ParsedStateCell {
  kind: string;
  spec: StateSpec;
  tone: Tone;
  label: string;
  glyph: string;
  suffixText: string;
  lines: string[];
}

/** A `state_cell` token read against the column's `states` map: the
 *  label, suffix and lines the cell shows. Shared by the render and the
 *  export, so the file carries the same words as the screen. ``null`` =
 *  empty cell; ``"undeclared"`` = a token the config never declared. */
function parseStateCell(
  raw: unknown,
  col: ColumnDef,
  lang: Lang,
): ParsedStateCell | null | "undeclared" {
  const token = raw == null ? "" : String(raw).trim();
  if (token === "") return null;
  const sep = token.indexOf(":");
  const kind = sep === -1 ? token : token.slice(0, sep);
  const suffixRaw = sep === -1 ? "" : token.slice(sep + 1);
  const spec = col.states?.[kind];
  if (!spec) return "undeclared";
  const tone: Tone = (TONES as ReadonlyArray<string>).includes(spec.tone)
    ? (spec.tone as Tone)
    : "neutral";
  const label = resolveLocalized(spec.label as DisplayLabel, lang) || kind;
  const glyph = spec.glyph || STATE_GLYPH_BY_TONE[tone];
  // A suffix may carry several values ("yes:67|91|59"): templates
  // read `{1}`, `{2}`… and `{n}` is the whole suffix.
  const parts = suffixRaw.split("|");
  const fill = (template: string): string =>
    template
      .replace(/\{n\}/g, suffixRaw)
      .replace(/\{(\d)\}/g, (_m, i: string) => parts[Number(i) - 1] ?? "");
  const lines = (spec.lines ?? [])
    .map((line) => fill(resolveLocalized(line as DisplayLabel, lang)))
    .filter((line) => line.trim() !== "");
  const suffixTemplate = spec.suffix ? resolveLocalized(spec.suffix as DisplayLabel, lang) : "";
  // With `lines` the raw suffix feeds them; it is not ALSO printed
  // inline unless a `suffix` template asks for it.
  const suffixText =
    suffixRaw === ""
      ? ""
      : suffixTemplate
        ? fill(suffixTemplate)
        : lines.length > 0
          ? ""
          : suffixRaw;
  return { kind, spec, tone, label, glyph, suffixText, lines };
}

/** What a `state_cell` exports: "Caído −12 d · 0/2 arriba" — the same
 *  text the server export writes (``table_export.state_cell_value_map``). */
function stateCellText(raw: unknown, col: ColumnDef, lang: Lang): string {
  const parsed = parseStateCell(raw, col, lang);
  if (parsed === null) return "";
  if (parsed === "undeclared") return String(raw);
  const head = parsed.suffixText ? `${parsed.label} ${parsed.suffixText}` : parsed.label;
  return [head, ...parsed.lines].join(" · ");
}

/** The text a cell shows, for a place that lists values instead of
 *  drawing the table (the detail of a task): a state token reads as its
 *  label and suffix, as in the export; a bilingual `{"en","es"}` cell in
 *  the active language; a number in the column's format. */
export function cellDisplayText(value: unknown, col: ColumnDef, lang: Lang): string {
  if (col.render === "state_cell") return stateCellText(value, col, lang);
  if (typeof value === "string") return resolveLocalized(value, lang);
  return formatCellValue(value, col.format);
}

/** Renders whose cell is a picture of other columns (a sparkline, a
 *  button, a stacked bar): nothing a spreadsheet can hold. */
const NON_EXPORTED_RENDERS: ReadonlySet<string> = new Set(["sparkline", "action", "mini_stack"]);

function renderMixedCell(
  raw: unknown,
  col: ColumnDef,
  row: Record<string, unknown>,
  ctx: MixedCellCtx,
): ReactNode | null {
  switch (col.render) {
    case "fraction": {
      const n = Number(raw);
      if (raw == null || raw === "" || !Number.isFinite(n)) {
        return <span className="text-neutral-300">—</span>;
      }
      const d = col.denominatorKey ? Number(row[col.denominatorKey]) : NaN;
      // No usable denominator → the bare figure, never an invented "/ 0".
      if (!Number.isFinite(d) || d <= 0) {
        return <span className="font-semibold tabular-nums">{formatCount(n)}</span>;
      }
      const pct = (100 * n) / d;
      return (
        <span className="inline-block text-right">
          <span className="font-semibold tabular-nums">
            {formatCount(n)}
            <span className="font-normal text-neutral-400"> / {formatCount(d)}</span>
          </span>
          <span className="block text-2xs font-normal leading-tight text-neutral-500">
            {formatFixed(pct, pct >= 99.95 || pct === 0 ? 0 : 1)} %
            {ctx.subText ? ` ${ctx.subText}` : ""}
          </span>
        </span>
      );
    }
    case "delta_pp": {
      const num = Number(raw);
      if (raw == null || raw === "" || !Number.isFinite(num)) {
        return <span className="text-neutral-300">—</span>;
      }
      // A `percent` column holds a fraction (0.032 → +3.2 pp); any other
      // format is already in points.
      const pp = col.format === "percent" ? num * 100 : num;
      if (Math.abs(pp) < 0.05) {
        return <span className="text-neutral-500 tabular-nums">{formatFixed(0, 1)} pp</span>;
      }
      const label = `${pp > 0 ? "+" : ""}${formatFixed(pp, 1)} pp`;
      return <DeltaChip delta={num} label={label} className="text-2xs" />;
    }
    case "mini_stack": {
      const parts = col.stack ?? [];
      const values = parts.map((part) => {
        const v = Number(row[part.key]);
        return Number.isFinite(v) && v > 0 ? v : 0;
      });
      const total = values.reduce((a, b) => a + b, 0);
      if (parts.length === 0 || total <= 0) {
        return <span className="text-neutral-300">—</span>;
      }
      const legend = parts
        .map((part, i) => {
          const name = resolveLocalized(part.label as DisplayLabel, ctx.lang) || part.key;
          return `${name}: ${formatCount(values[i]!)} (${((100 * values[i]!) / total).toFixed(0)} %)`;
        })
        .join(" · ");
      return (
        <span className="inline-flex items-center align-middle" title={legend}>
          <span className="relative inline-flex h-2 w-24 overflow-hidden rounded-full bg-neutral-200">
            {parts.map((part, i, all) =>
              values[i]! > 0 ? (
                <span
                  key={part.key}
                  data-stack-part={part.key}
                  className="h-full"
                  style={{
                    width: `${(100 * values[i]!) / total}%`,
                    // A state takes its tone's v3 colour; a category the next
                    // series colour (look v3, DQ2: configs carry no hex).
                    background: part.tone
                      ? toneStyle(part.tone).fill
                      : STACK_SERIES_COLORS[all.slice(0, i).filter((p) => !p.tone).length % STACK_SERIES_COLORS.length],
                  }}
                />
              ) : null,
            )}
          </span>
          {/* The breakdown as text: a colourblind or screen-reader user
              gets the same numbers the bar encodes. */}
          <span className="sr-only">{legend}</span>
        </span>
      );
    }
    case "state_cell": {
      // The SQL emits a token, optionally with a numeric suffix
      // ("fallen:12"). EVERYTHING else — which tokens exist, their tone,
      // glyph, label and how the number reads — comes from the column's
      // authored `states` map. The render owns no project vocabulary;
      // null = no row behind this cell → a dot, not a state.
      const parsed = parseStateCell(raw, col, ctx.lang);
      if (parsed === null) return <span className="text-neutral-300">·</span>;
      if (parsed === "undeclared") {
        const token = String(raw).trim();
        const kind = token.includes(":") ? token.slice(0, token.indexOf(":")) : token;
        // Fail visibly (same rule as `tone` without bands): a token the
        // config never declared is a config error, not a value to print.
        return (
          <span
            data-state-cell="undeclared"
            className="text-danger-600"
            title={`render:'state_cell' — token '${kind}' is not in states`}
          >
            ⚠ {kind}
          </span>
        );
      }
      const { kind, spec, tone, label, glyph, suffixText, lines } = parsed;
      const palette = toneStyle(tone);
      // Look v3: glyph + word. With the word shown, a neutral state needs
      // no shape in front of it (the default "—" would read as "no data").
      const shape = spec.show_label && tone === "neutral" && !spec.glyph ? "" : glyph;
      const pill = (
        <span
          data-state-cell={kind}
          title={[label, suffixText, ...lines].filter(Boolean).join(" · ")}
          className="inline-flex items-center gap-1 whitespace-nowrap text-2xs font-semibold leading-4"
          style={{ color: palette.fg }}
        >
          {shape && (
            <span aria-hidden="true" className="font-extrabold">
              {shape}
            </span>
          )}
          {spec.show_label ? <span>{label}</span> : <span className="sr-only">{label}</span>}
          {suffixText && <span className="tabular-nums">{suffixText}</span>}
        </span>
      );
      if (col.cellStyle === "fill") {
        // The prototype's matrix cell: the whole cell carries the state
        // (tinted, 2px tone border on the left), glyph + suffix bold,
        // the lines muted under it. The label stays for screen readers.
        return (
          <span
            data-state-cell={kind}
            data-cell-style="fill"
            title={[label, suffixText, ...lines].filter(Boolean).join(" · ")}
            className="-mx-1.5 -my-1 flex min-w-[3rem] flex-col items-center justify-center px-1.5 py-1 text-[11px] font-extrabold tabular-nums"
            style={{
              color: palette.fg,
              background: tone === "neutral" ? undefined : palette.bg,
              borderLeft: tone === "neutral" ? undefined : `2px solid ${palette.fill}`,
            }}
          >
            <span>
              {spec.show_label ? label : <span aria-hidden="true">{glyph}</span>}
              {!spec.show_label && <span className="sr-only">{label}</span>}
              {suffixText && <span className="ml-0.5">{suffixText}</span>}
            </span>
            {/* The lines are data ("3/5"): full tone colour, set apart
                by weight. `opacity-70` took them to 2.9:1 to 4.1:1 on the
                tint (detector, 2026-10-24), under the 4.5:1 floor. */}
            {lines.map((line, i) => (
              <span key={i} data-state-line className="text-2xs font-medium leading-tight">
                {line}
              </span>
            ))}
          </span>
        );
      }
      if (lines.length === 0) return pill;
      return (
        <span className="inline-flex flex-col items-start gap-0.5">
          {pill}
          {lines.map((line, i) => (
            <span
              key={i}
              data-state-line
              className="text-2xs font-normal leading-tight text-neutral-500 tabular-nums"
            >
              {line}
            </span>
          ))}
        </span>
      );
    }
    case "action": {
      const value = raw == null ? "" : String(raw);
      if (!col.filterKey || !ctx.onCellFilter || value === "") {
        return <span className="text-neutral-300">—</span>;
      }
      const filterKey = col.filterKey;
      const apply = ctx.onCellFilter;
      const active = crossFilterActiveValue(ctx.activeFilters?.[filterKey]) === value;
      return (
        <button
          type="button"
          aria-pressed={active}
          onClick={(e) => {
            e.stopPropagation();
            apply(filterKey, value);
          }}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-2xs font-semibold transition-colors ${
            active
              ? "border-accent bg-accent text-white hover:bg-accent-dark"
              : "border-neutral-300 bg-white text-neutral-700 hover:border-accent hover:text-accent-dark"
          }`}
        >
          <Filter size={10} aria-hidden="true" />
          {ctx.t(active ? "table.action.clear" : "table.action.filter")}
        </button>
      );
    }
    default:
      return null;
  }
}

/** A sparkline cell's series: `[1, 2, 3]` or `[{d, v}, …]`. Labels
 *  come back only when EVERY point carries one — a half-labelled hover
 *  would name some days and not others. Points whose value is not a
 *  finite number are dropped (a gap, never a zero). */
export function parseSparklineSeries(
  raw: unknown,
): { values: number[]; labels: string[] | null } | null {
  if (!Array.isArray(raw)) return null;
  const values: number[] = [];
  const labels: string[] = [];
  let labelled = 0;
  for (const item of raw as unknown[]) {
    if (typeof item === "number") {
      if (Number.isFinite(item)) values.push(item);
      continue;
    }
    if (item !== null && typeof item === "object" && !Array.isArray(item)) {
      const o = item as Record<string, unknown>;
      const v = Number(o["v"] ?? o["value"]);
      if (!Number.isFinite(v)) continue;
      const l = o["d"] ?? o["label"] ?? o["date"];
      values.push(v);
      if (l != null && String(l) !== "") {
        labels.push(String(l));
        labelled += 1;
      } else {
        labels.push("");
      }
    }
  }
  return { values, labels: labelled === values.length && labelled > 0 ? labels : null };
}

/** The historical ``default`` cell renderer — also the strict
 *  fallback for ``trend`` cells whose text didn't parse. */
function renderDefaultCell(
  raw: unknown,
  col: ColumnDef,
  linkLabel: string,
  lang: Lang,
): ReactNode {
  // Iter 10b — auto-detect URL-shaped strings (any column whose
  // value starts with http(s)://) and render as a click-out link.
  // Keeps the cell narrow and avoids spamming long Amazon URLs in
  // the table. Click stops propagation so the row's onRowClick
  // (when set) doesn't fire alongside opening the link.
  if (typeof raw === "string" && /^https?:\/\//i.test(raw)) {
    return (
      <a
        href={raw}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-accent-dark hover:text-accent-dark underline decoration-dotted"
      >
        {linkLabel}
      </a>
    );
  }
  // Redesign rule 4: a cell whose ENTIRE text is a bare arrow+delta
  // token ("▲ +2.0%", "▼ -9.6%", "▲ +0.5 pp") — no value prefix, so the
  // strict `trend`/`parseValueDelta` path doesn't own it — renders as
  // the semantic pastel DeltaChip. STRICT: `parseDeltaOnly` returns null
  // for anything else (bilingual JSON, a bare "—", free text, a
  // "value ▲ delta" string) → those fall through to the exact rendering
  // below. Style upgrades, data never deforms.
  if (typeof raw === "string") {
    const deltaOnly = parseDeltaOnly(raw);
    if (deltaOnly) {
      return (
        <DeltaChip
          delta={deltaOnly.dir === "up" ? 1 : -1}
          label={deltaOnly.delta}
          className="text-2xs"
        />
      );
    }
  }
  // Iter 16: when the SQL emits a JSON-encoded bilingual string
  // (``{"en": "...", "es": "..."}``) — e.g. gating_executive
  // pattern column — unwrap it via resolveLocalized so the user
  // sees the active locale. Plain strings round-trip unchanged
  // (the helper short-circuits when there's no ``{"en"`` /
  // ``{"es"`` prefix).
  if (typeof raw === "string") {
    const resolved = resolveLocalized(raw as DisplayLabel, lang);
    // NOTE (2026-07-14): the value-based semantic styling that lived
    // here (YES/NO badges, Entered/Exited/Migrated dot-pills from PR
    // #60's semantic-cells.tsx) was REMOVED on user feedback — auto-
    // styling by cell value reads inconsistent with real data (mixed
    // "1P" plain / "NO" badge in the same STATUS column). Text values
    // render as plain text. If a specific column needs semantic color,
    // implement it as an opt-in per-column ``render`` variant in the
    // report config (like ``render: "trend"``) — never value detection.
    if (isIsoDateString(raw)) {
      // ISO dates: muted, tabular (alignment handled at the td level).
      return <span className="text-neutral-500">{raw.trim()}</span>;
    }
    if (resolved && resolved !== raw) {
      return <span>{resolved}</span>;
    }
  }
  if (raw == null) {
    // formatValue renders null/empty as "—" — style it dimmed so
    // missing values read as absence, not data.
    return <span className="text-neutral-300">—</span>;
  }
  return <span>{formatCellValue(raw, col.format)}</span>;
}

// Redesign v2: heat cells ramp on the shared magenta HEAT_RAMP
// (accent 50→900, same scale as HeatmapTable / HeatmapChart / maps)
// instead of the legacy morado rgba ladder. Dark ramp steps flip the
// cell text to white (inline style so it wins over the td class).
function heatStyleFor(
  raw: unknown,
  range: { min: number; max: number } | undefined,
): { backgroundColor: string; color?: string; fontWeight?: number } | undefined {
  if (!range) return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n)) return undefined;
  const span = range.max - range.min;
  const t = span === 0 ? 0.5 : (n - range.min) / span;
  const idx = Math.min(
    HEAT_RAMP.length - 1,
    Math.max(0, Math.floor(Math.max(0, Math.min(1, t)) * HEAT_RAMP.length)),
  );
  return {
    backgroundColor: HEAT_RAMP[idx]!,
    color: idx >= 3 ? "white" : undefined,
    fontWeight: 600,
  };
}
